import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, Eye, Copy } from "lucide-react";
import { toast } from "sonner";

export default function PreviewAsSection() {
  const [busy, setBusy] = useState("");
  const [link, setLink] = useState(null);

  const generate = async (role) => {
    setBusy(role); setLink(null);
    try {
      const r = await api.post(`/auth/preview-token?role=${role}`);
      const url = `${window.location.origin}/api/auth/preview-login?token=${r.data.session_token}`;
      setLink({ ...r.data, login_url: url, generated_at: Date.now() });
      toast.success(`Preview link generated for ${role}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const revokeAll = async () => {
    if (!window.confirm("Revoke ALL outstanding preview links?")) return;
    try {
      const r = await api.post("/auth/preview-revoke");
      toast.success(`Revoked ${r.data.revoked} preview session${r.data.revoked === 1 ? "" : "s"}`);
      setLink(null);
    } catch { toast.error("Failed"); }
  };

  const copy = async () => {
    if (!link?.login_url) return;
    try { await navigator.clipboard.writeText(link.login_url); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <section className="border border-slate-200 rounded-sm" data-testid="preview-as-section">
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <Eye size={14} className="text-slate-500" />
        <h2 className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>Preview as Manager / Executive</h2>
      </div>
      <div className="px-5 py-4 space-y-4">
        <p className="text-xs text-slate-600 leading-relaxed">
          Generate a short-lived magic URL (30 minutes) to log into the portal as a synthetic
          test <span className="font-mono-data">Manager</span> or <span className="font-mono-data">Executive</span>.
          Open the URL in an <em>incognito</em> window so it doesn't overwrite your Admin session.
          A "Preview Mode" banner is shown at the top of every page when signed in as one of these users.
          Both accounts share a single sample client, project, tasks, and check-in record so the dashboards aren't empty.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => generate("Manager")}
            disabled={!!busy}
            className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm"
            data-testid="btn-preview-manager"
          >
            <Eye size={14} className="mr-1.5" /> {busy === "Manager" ? "Generating…" : "Preview as Manager"}
          </Button>
          <Button
            onClick={() => generate("Executive")}
            disabled={!!busy}
            variant="outline"
            className="rounded-sm"
            data-testid="btn-preview-executive"
          >
            <Eye size={14} className="mr-1.5" /> {busy === "Executive" ? "Generating…" : "Preview as Executive"}
          </Button>
          <Button onClick={revokeAll} variant="ghost" className="rounded-sm text-red-600 hover:text-red-700" data-testid="btn-preview-revoke">
            Revoke all
          </Button>
        </div>

        {link && (
          <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 space-y-2" data-testid="preview-link-card">
            <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold">
              {link.role} preview link · valid for {link.expires_in_minutes} min
            </div>
            <div className="flex items-stretch gap-2">
              <Input
                readOnly
                value={link.login_url}
                className="font-mono-data text-xs rounded-sm bg-white"
                data-testid="input-preview-url"
                onFocus={(e) => e.target.select()}
              />
              <Button onClick={copy} variant="outline" className="rounded-sm shrink-0" data-testid="btn-copy-preview-url">
                <Copy size={12} className="mr-1" /> Copy
              </Button>
              <a
                href={link.login_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-3 rounded-sm border border-slate-200 hover:border-[#0a2540] text-xs shrink-0"
                data-testid="link-preview-open"
              >
                <ExternalLink size={12} /> Open
              </a>
            </div>
            <p className="text-[11px] text-amber-800">
              Tip: right-click <em>Open</em> → "Open in incognito window" so your Admin session stays intact.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
